import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Wallet, Clock, Zap, Gift, Activity, Trophy, ExternalLink, Coins, CheckCircle2 } from 'lucide-react';


// --- KONFIGURATION ---
const CONTRACT_ADDRESS = "0x0e336248d5d3e2AA6Ea2795D3039F601008C9D5a";
const LUCIS_TOKEN_ADDR = "0x2E66AC6c22E8Bf69B627FC0DEabb1Db0A5e93740";
const SHEITAN_TOKEN_ADDR = "0xc18844B930E7075c388a583441E18E3E11Ef93aa";
const IOTA_MAINNET_HEX_ID = "0x2276"; 
const WIN_SOUND_URL = "https://assets.mixkit.co";

const TOKEN_ICONS = { lucis: "/lis.png", sheitan: "/sen.png" };
const POLL_IMAGES = {
  "1": "/iota.png",
  "2": "/btc.png",
  "3": "/updown.png",
  "4": "/weather.png",
  "5": "/social.jpg",
  "6": "/music.png",
  "7": "/rocket.png",
  "8": "/peace.png",
  "9": "/weather.png",
  "10": "/peace.png",
  "11": "/weather.png",
  "12": "/weather.png",
  "13": "/ecb.jpg",
  "14": "/rocket.jpg",
  "15": "/football.jpg",
  "16": "/eth.jpg",
  "17": "/open.jpg",
  "18": "/ballbasket.png",
  "19": "/btc.png",
  "20": "/football.jpg",
  "21": "/hok.jpg",
  "22": "/updown.png",
  "23": "/dust.jpg",
  "24": "/sanan.jpg",
  "25": "/gamefest.jpg",
  "26": "/btc.png",
  "27": "/updown.png",
  "28": "/ecb.jpg",
  "29": "/rate.png",
  "30": "/rocket.png",
  "31": "/peace.png",
  "32": "/gamefest.jpg",
  "33": "/gamefest.jpg",
  "34": "/football.jpg",
  
  
  
};

// MODERNE INFO-KOMPONENTE (Erklärt den Contract Flow)
const ContractLogicInfo = () => {
    const steps = [
        { icon: <Zap className="text-yellow-400" />, title: "Choose Side", desc: "Vote for LUCIS (Yes) or SHEITAN (No) with the required stake." },
        { icon: <Activity className="text-purple-400" />, title: "The Battle", desc: "Stakes are locked in the contract until the countdown ends." },
        { icon: <Trophy className="text-green-400" />, title: "Winner Rewards", desc: "Winners get their stake back plus a proportional share (95%) of the loser pool." },
        { icon: <Coins className="text-rose-400" />, title: "Burn & Deflation", desc: "4% of losing stakes are burned forever, 1% goes to development." }
    ];

    return (
        <section className="py-8 px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {steps.map((step, idx) => (
                    <motion.div 
                        key={idx} 
                        initial={{ opacity: 0, y: 20 }} 
                        whileInView={{ opacity: 1, y: 0 }} 
                        transition={{ delay: idx * 0.1 }}
                        className="bg-white/5 border border-white/10 p-5 rounded-2xl backdrop-blur-md highroller-glow"
                    >
                        <div className="bg-black/40 w-10 h-10 rounded-xl flex items-center justify-center mb-3 shadow-lg">
                            {step.icon}
                        </div>
                        <h3 className="text-[11px] font-black uppercase italic tracking-widest mb-1">{step.title}</h3>
                        <p className="text-[10px] text-gray-400 leading-tight font-medium">
                            {step.desc}
                        </p>
                    </motion.div>
                ))}
            </div>
        </section>
    );
};

const VOTING_ABI = [
    {"inputs":[{"internalType":"uint256","name":"_pollId","type":"uint256"}],"name":"claim","outputs":[],"stateMutability":"nonpayable","type":"function"},
    {"inputs":[{"internalType":"uint256","name":"_pollId","type":"uint256"},{"internalType":"bool","name":"_isLucis","type":"bool"}],"name":"vote","outputs":[],"stateMutability":"nonpayable","type":"function"},
    {"inputs":[{"internalType":"uint256","name":"_pollId","type":"uint256"},{"internalType":"address","name":"_u","type":"address"}],"name":"getStatus","outputs":[{"internalType":"uint256","name":"lucis","type":"uint256"},{"internalType":"uint256","name":"sheitan","type":"uint256"},{"internalType":"bool","name":"vtd","type":"bool"},{"internalType":"bool","name":"clmd","type":"bool"}],"stateMutability":"view","type":"function"},
    {"inputs":[],"name":"pollCount","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},
    {"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"polls","outputs":[{"internalType":"string","name":"question","type":"string"},{"internalType":"string","name":"category","type":"string"},{"internalType":"uint256","name":"endTime","type":"uint256"},{"internalType":"uint256","name":"requiredLucis","type":"uint256"},{"internalType":"uint256","name":"requiredSheitan","type":"uint256"},{"internalType":"bool","name":"resolved","type":"bool"},{"internalType":"uint8","name":"winner","type":"uint8"},{"internalType":"uint256","name":"totalLucisDeposited","type":"uint256"},{"internalType":"uint256","name":"totalSheitanDeposited","type":"uint256"}],"stateMutability":"view","type":"function"}
];

const ERC20_ABI = ["function approve(address spender, uint256 amount) public returns (bool)", "function allowance(address owner, address spender) view returns (uint256)", "function balanceOf(address account) view returns (uint256)"];

function App() {
    const [account, setAccount] = useState("");
    const [hasConsented, setHasConsented] = useState(localStorage.getItem("lissen_consent") === "true");
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorPopup, setErrorPopup] = useState(null);

    const [pollsList, setPollsList] = useState([]);
    const [userStatus, setUserStatus] = useState({});
    const [activeFilter, setActiveFilter] = useState("All");
    const [now, setNow] = useState(Math.floor(Date.now() / 1000));
    const [stats, setStats] = useState({ wins: 0, losses: 0 });
    const [balances, setBalances] = useState({ lucis: "0", sheitan: "0" });
    const [activeSection, setActiveSection] = useState("All"); // For Cash, Politics, Freedom
    const [isInitialLoading, setIsInitialLoading] = useState(false); // New loading state



    // --- INSERT THESE TWO HERE ---
    const pollRefs = React.useRef({}); 

    const scrollToPoll = (id) => {
        const element = pollRefs.current[id];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.style.transition = "filter 0.5s";
            element.style.filter = "brightness(1.5)";
            setTimeout(() => element.style.filter = "none", 800);
        }
    };
    // -----------------------------

    useEffect(() => {
        const timer = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
        return () => clearInterval(timer);
    }, []);
    
    // ... rest of your code


    const topPots = useMemo(() => 
        [...pollsList].filter(p => !p.resolved)
        .sort((a, b) => {
            const totalA = window.BigInt(a.totalLucis) + window.BigInt(a.totalSheitan);
            const totalB = window.BigInt(b.totalLucis) + window.BigInt(b.totalSheitan);
            return totalB > totalA ? 1 : totalB < totalA ? -1 : 0;
        })
        .slice(0, 10)
    , [pollsList]);

    const loadData = useCallback(async (vContract, userAddr) => {
        if (!vContract || !userAddr) return;
        try {
            const count = Number(await vContract.pollCount());
            const provider = new ethers.BrowserProvider(window.ethereum);
            const signer = await provider.getSigner();
            const lucisToken = new ethers.Contract(LUCIS_TOKEN_ADDR, ERC20_ABI, signer);
            const sheitanToken = new ethers.Contract(SHEITAN_TOKEN_ADDR, ERC20_ABI, signer);

            const [bL, bS] = await Promise.all([lucisToken.balanceOf(userAddr), sheitanToken.balanceOf(userAddr)]);
            setBalances({ 
                lucis: ethers.formatUnits(bL.toString(), 18), 
                sheitan: ethers.formatUnits(bS.toString(), 18) 
            });

            const list = []; const statusMap = {}; let wins = 0, losses = 0;
            for (let i = 1; i <= count; i++) {
                const p = await vContract.polls(i);
                const pollData = { 
                    id: i, question: p.question, category: p.category, 
                    endTime: Number(p.endTime), 
                    totalLucis: p.totalLucisDeposited.toString(), 
                    totalSheitan: p.totalSheitanDeposited.toString(), 
                    resolved: p.resolved, winner: Number(p.winner), 
                    reqL: p.requiredLucis.toString(), reqS: p.requiredSheitan.toString() 
                };
                list.push(pollData);

                const s = await vContract.getStatus(i, userAddr);
                statusMap[i] = { voted: s.vtd, claimed: s.clmd, uL: s.lucis.toString(), uS: s.sheitan.toString() };
                
                if (pollData.resolved && s.vtd) {
                    const hasLucis = ethers.toBigInt(s.lucis) > 0n;
                    const hasSheitan = ethers.toBigInt(s.sheitan) > 0n;
                    ((pollData.winner === 1 && hasLucis) || (pollData.winner === 2 && hasSheitan)) ? wins++ : losses++;
                }
            }
            setPollsList(list.reverse()); 
            setUserStatus(statusMap); 
            setStats({ wins, losses });
        } catch (e) { console.error("LoadData Error:", e); }
    }, []);

    const acceptTerms = () => {
        localStorage.setItem("lissen_consent", "true");
        setHasConsented(true);
    };

    // const acceptTerms = () => 

        // ... Ende von acceptTerms ...

       const connectWallet = async () => {
    if (!window.ethereum) return alert("Please install MetaMask!");
    
    try {
        // --- 1. START LOADING ---
        setIsInitialLoading(true); 

        // Netzwerk-Check & Switch zu IOTA EVM
        try {
            await window.ethereum.request({ 
                method: 'wallet_switchEthereumChain', 
                params: [{ chainId: IOTA_MAINNET_HEX_ID }] 
            });
        } catch (switchError) {
            if (switchError.code === 4902) {
                await window.ethereum.request({
                    method: 'wallet_addEthereumChain',
                    params: [{
                        chainId: IOTA_MAINNET_HEX_ID,
                        chainName: 'IOTA EVM',
                        nativeCurrency: { name: 'IOTA', symbol: 'IOTA', decimals: 18 },
                        rpcUrls: ['https://json-rpc.evm.iotaledger.net'],
                        blockExplorerUrls: ['https://explorer.evm.iota.org']
                    }],
                });
            } else {
                throw switchError;
            }
        }

        // Wallet verbinden
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        const userAddr = accounts[0]; 
        
        // Ethers Provider & Contract Setup
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const vContract = new ethers.Contract(CONTRACT_ADDRESS, VOTING_ABI, signer);
        
        setAccount(userAddr); 
        setContract(vContract); 

        // --- 2. WAIT FOR DATA TO FINISH ---
        await loadData(vContract, userAddr);

    } catch (err) { 
        console.error("Connection error:", err); 
    } finally {
        // --- 3. STOP LOADING ---
        setIsInitialLoading(false); 
    }
};


    // Filter-Logik (Nur EINMAL definiert, um "Cannot redeclare" Fehler zu vermeiden)
       // 1. Die Filter-Logik (NUR EINMAL)
const filteredPolls = useMemo(() => {
    return pollsList.filter(p => {
        const status = userStatus[p.id];
        const uL = window.BigInt(status?.uL || "0");
        const uS = window.BigInt(status?.uS || "0");
        const hasVoted = status?.voted || uL > 0n || uS > 0n;
        const winnerId = Number(p.winner);

        // --- NEU: Kategorie-String splitten ---
        // Wenn p.category "Politics-Highroller" ist:
        const [categoryPart, difficultyPart] = (p.category || "").split("-");

        // 1. SECTION FILTER
        // Wir prüfen, ob der erste Teil des Strings mit der activeSection übereinstimmt
        const matchesSection = activeSection === "All" || categoryPart === activeSection;
        
        // 2. STATUS & PERFORMANCE FILTER
        let matchesStatus = true;

        if (activeFilter === "Wins") {
            matchesStatus = p.resolved && ((winnerId === 1 && uL > 0n) || (winnerId === 2 && uS > 0n));
        } 
        else if (activeFilter === "Losses") {
            const won = (winnerId === 1 && uL > 0n) || (winnerId === 2 && uS > 0n);
            matchesStatus = p.resolved && hasVoted && !won;
        } 
        else if (activeFilter === "Highroller") {
            // Entweder du bleibst beim Pot-Wert ODER du nutzt den Tag aus dem Admin-Panel:
            matchesStatus = difficultyPart === "Highroller";
        } 
        else if (activeFilter === "Easy") {
            // Hier ebenfalls: Prüfung auf den Tag vom Admin-Panel
            matchesStatus = difficultyPart === "Easy";
        }

        return matchesSection && matchesStatus;
    });
}, [pollsList, activeFilter, activeSection, userStatus]);




const handleAction = async (id, isLucis, isClaim = false) => {
    if (!contract) return connectWallet();
    
    try {
        setLoading(true);
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();

        if (isClaim) {
            const tx = await contract.claim(id);
            await tx.wait();
            new Audio(WIN_SOUND_URL).play().catch(() => {});
            if (typeof confetti === 'function') confetti({ particleCount: 150, spread: 70 });
        } else {
            const poll = pollsList.find(p => p.id === id);
            if (!poll) throw new Error("Poll not found");

            // --- CORRECTED BALANCE CONVERTER ---
            const getSafeBal = (val) => {
                if (!val) return 0n;
                const s = val.toString();
                
                // If it's a human number (has a dot or is small), parse it to Wei
                if (s.includes('.') || parseFloat(s) < 1000000000) {
                    // Safety: limit to 18 decimals to prevent ethers error
                    const [int, frac] = s.split('.');
                    const fixed = frac ? `${int}.${frac.slice(0, 18)}` : int;
                    return ethers.parseUnits(fixed, 18);
                }
                
                // Otherwise, it's a massive integer string (already Wei)
                return window.BigInt(s);
            };

            const reqL = window.BigInt(poll.reqL);
            const reqS = window.BigInt(poll.reqS);

            // Separate checks so LIS doesn't block SEN and vice-versa
            if (isLucis) {
                const userL = getSafeBal(balances.lucis);
                if (userL < reqL) {
                    setErrorPopup(`Insufficient LIS. Have: ${ethers.formatUnits(userL, 18)} / Need: ${ethers.formatUnits(reqL, 18)}`);
                    setLoading(false);
                    return;
                }
            } else {
                const userS = getSafeBal(balances.sheitan);
                if (userS < reqS) {
                    setErrorPopup(`Insufficient SEN. Have: ${ethers.formatUnits(userS, 18)} / Need: ${ethers.formatUnits(reqS, 18)}`);
                    setLoading(false);
                    return;
                }
            }

            // --- TRANSACTIONS ---
            const amount = isLucis ? poll.reqL : poll.reqS;
            const tokenAddr = isLucis ? LUCIS_TOKEN_ADDR : SHEITAN_TOKEN_ADDR;
            const tCon = new ethers.Contract(tokenAddr, ERC20_ABI, signer);
            
            const appTX = await tCon.approve(CONTRACT_ADDRESS, amount);
            await appTX.wait();
            
            const voteTX = await contract.vote(id, isLucis);
            await voteTX.wait();
        }
        await loadData(contract, account);
    } catch (e) {
        console.error("Action failed:", e);
        setErrorPopup(e.reason || e.message || "Transaction failed");
    } finally {
        setLoading(false);
    }
};


    // 2. Share Function (Generates link like lissen.vote/#poll-5)
    const handleShare = (id) => {
        const shareUrl = `${window.location.origin}${window.location.pathname}#poll-${id}`;
        navigator.clipboard.writeText(shareUrl).then(() => {
            alert("Battle Link copied to clipboard! ⚔️");
        }).catch(err => console.error("Clipboard error:", err));
    };

    // 3. Auto-Scroll Hook (Scrolls to poll when opening a shared link)
    useEffect(() => {
        const hash = window.location.hash;
        if (hash && hash.startsWith('#poll-') && pollsList.length > 0) {
            const id = hash.replace('#poll-', '');
            const timer = setTimeout(() => scrollToPoll(id), 1200);
            return () => clearTimeout(timer);
        }
    }, [pollsList.length]);

    // 4. Auto-Refresh Hook (Syncs with Admin Panel updates every 30s)
    useEffect(() => {
        if (contract && account) {
            const interval = setInterval(() => loadData(contract, account), 30000);
            return () => clearInterval(interval);
        }
    }, [contract, account, loadData]);

    // 5. Disclaimer Logic
    if (!hasConsented) {
        return (
            <div className="fixed inset-0 z-[9999] bg-[#020203] flex items-center justify-center p-6 text-white text-center">
                <div className="max-w-md w-full bg-[#0d0d12] border border-purple-500/30 p-10 rounded-[3rem] shadow-2xl shadow-purple-500/10">
                    <h2 className="text-2xl font-black uppercase italic mb-6 tracking-tighter text-purple-500">Web3 Access Agreement</h2>
                    <div className="text-[10px] text-gray-400 space-y-4 text-left uppercase font-black mb-10 italic leading-relaxed">
                        <p><span className="text-purple-400 font-bold">01. No Financial Advice:</span> This app is a social experiment. Tokens used here have no monetary value.</p>
                        <p><span className="text-purple-400 font-bold">02. Risk:</span> Smart contracts are experimental software. Use at your own risk.</p>
                        <p><span className="text-purple-400 font-bold">03. Finality:</span> All transactions are irreversible once signed in your wallet.</p>
                    </div>
                    <button 
                        onClick={acceptTerms} 
                        className="w-full bg-purple-600 hover:bg-purple-500 text-white font-black py-5 rounded-full transition-all uppercase italic tracking-widest shadow-xl active:scale-95 shadow-purple-500/20"
                    >
                        I UNDERSTAND & AGREE
                    </button>
                </div>
            </div>
        );
    }


       return (
        <div className="min-h-screen bg-[#020203] text-white selection:bg-purple-500/30">
            {/* HEADER */}
            <header className="p-4 md:p-6 flex justify-between items-center border-b border-white/5 bg-black/40 backdrop-blur-2xl sticky top-0 z-50">
                <div className="flex items-center gap-2 md:gap-3">
                    <div className="bg-purple-600 p-1.5 md:p-2 rounded-xl shadow-lg"><Zap size={20} className="md:w-6 md:h-6" fill="white" /></div>
                    <h1 className="text-lg md:text-2xl font-black italic uppercase tracking-tighter">LiSSEN VOTE</h1>
                </div>

                <div className="flex items-center gap-3 md:gap-8">
                    <div className="flex gap-3 md:gap-6 items-center">
                        <div className="flex items-center gap-1.5 md:gap-3">
                            <motion.img src={TOKEN_ICONS.lucis} alt="LIS" className="w-6 h-6 md:w-8 md:h-8 rounded-full shadow-lg shadow-purple-500/20 animate-token" />
                            <div className="text-right leading-tight">
                                <p className="text-[7px] md:text-[9px] font-black text-gray-500 uppercase">Lucis</p>
                                <p className="text-[9px] md:text-xs font-bold text-purple-400 italic">{Number(balances.lucis).toFixed(1)} <span className="hidden sm:inline">LIS</span></p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 md:gap-3">
                            <motion.img src={TOKEN_ICONS.sheitan} alt="SEN" className="w-6 h-6 md:w-8 md:h-8 rounded-full shadow-lg shadow-rose-500/20 animate-token" style={{animationDelay: '0.5s'}} />
                            <div className="text-right leading-tight">
                                <p className="text-[7px] md:text-[9px] font-black text-gray-500 uppercase">Sheitan</p>
                                <p className="text-[9px] md:text-xs font-bold text-rose-400 italic">{Number(balances.sheitan).toFixed(1)} <span className="hidden sm:inline">SEN</span></p>
                            </div>
                        </div>
                    </div>

                    <button onClick={connectWallet} className="flex items-center gap-1 md:gap-2 bg-white/5 hover:bg-purple-600 px-3 py-2 md:px-4 md:py-2 rounded-xl border border-white/10 transition-all text-[8px] md:text-[10px] font-black uppercase italic tracking-widest whitespace-nowrap">
                        <Wallet size={14} className="md:w-4 md:h-4" /> 
                        {account ? `${account.substring(0,6)}...` : <span className="hidden sm:inline">Connect EVM Wallet</span>}
                        {!account && <span className="sm:hidden">Wallet</span>}
                    </button>
                </div>
            </header>

            <ContractLogicInfo />

            <main className="max-w-7xl mx-auto px-6 py-4">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* SIDEBAR */}
<aside className="lg:col-span-1 space-y-6">
    {account ? (
        <>
            <div className="bg-[#0d0d12] rounded-[2.5rem] p-6 border border-white/5 shadow-2xl">
                <div className="flex items-center gap-2 mb-4 text-gray-500 font-black uppercase text-[10px] tracking-widest">
                    <Activity size={16} className="text-purple-500"/> Performance
                </div>
                <div className="space-y-3">
                    {/* WINS CLICKABLE BUTTON */}
                    <button 
                        onClick={() => setActiveFilter("Wins")}
                        className={`w-full flex justify-between items-center p-4 rounded-2xl border transition-all active:scale-95 ${
                            activeFilter === "Wins" 
                            ? "bg-emerald-500 text-black border-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.3)]" 
                            : "bg-emerald-500/5 border-emerald-500/10 text-emerald-500 hover:bg-emerald-500/10"
                        } text-xs font-bold`}
                    >
                        <span className="italic tracking-widest">WINS</span>
                        <span className={activeFilter === "Wins" ? "opacity-100" : "opacity-60"}>{stats.wins}</span>
                    </button>

                    {/* LOSSES CLICKABLE BUTTON */}
                    <button 
                        onClick={() => setActiveFilter("Losses")}
                        className={`w-full flex justify-between items-center p-4 rounded-2xl border transition-all active:scale-95 ${
                            activeFilter === "Losses" 
                            ? "bg-rose-500 text-white border-rose-400 shadow-[0_0_20px_rgba(244,63,94,0.3)]" 
                            : "bg-rose-500/5 border-rose-500/10 text-rose-500 hover:bg-rose-500/10"
                        } text-xs font-bold`}
                    >
                        <span className="italic tracking-widest">LOSSES</span>
                        <span className={activeFilter === "Losses" ? "opacity-100" : "opacity-60"}>{stats.losses}</span>
                    </button>

                    {/* RESET OPTION (Erscheint nur wenn ein Filter aktiv ist) */}
                    {activeFilter !== "All" && (
                        <button 
                            onClick={() => setActiveFilter("All")}
                            className="w-full text-[8px] text-gray-500 hover:text-white transition-colors uppercase font-black tracking-widest pt-2"
                        >
                            [ Show All Polls ]
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-[#0d0d12] rounded-[2.5rem] p-6 border border-white/5 shadow-2xl">
                {/* ... Top Pots bleibt gleich ... */}
                <div className="flex items-center gap-2 mb-4 text-gray-500 font-black uppercase text-[10px] tracking-widest">
                    <Trophy size={16} className="text-amber-500"/> Top Pots
                </div>
                <div className="space-y-2">
                    {topPots.map((p, i) => {
                        const sumBI = window.BigInt(p.totalLucis) + window.BigInt(p.totalSheitan);
                        return (
                            <div key={p.id} onClick={() => scrollToPoll(p.id)} className="flex justify-between items-center bg-white/5 p-3 rounded-xl border border-white/5 text-[10px] font-black italic uppercase cursor-pointer hover:bg-white/10 hover:border-purple-500/50 transition-all group">
                                <span className="opacity-60 group-hover:text-purple-400">#{i+1} Pot</span>
                                <span className="text-purple-400">{ethers.formatUnits(sumBI.toString(), 18).split('.')[0]} LIS/SEN</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    ) : (
        /* ... Unconnected State ... */
        <div className="bg-[#0d0d12] rounded-[2.5rem] p-6 border border-dashed border-white/10 text-center opacity-50">
            <p className="text-[9px] font-black uppercase italic text-gray-500">Connect wallet to unlock stats</p>
        </div>
    )}

                        <div className="bg-gradient-to-br from-purple-900/40 to-black rounded-[2.5rem] p-6 border border-purple-500/20 shadow-2xl">
                            <div className="flex items-center gap-2 mb-3 text-purple-400 font-black uppercase text-[10px] tracking-widest"><Coins size={16}/> Ecosystem</div>
                            <div className="space-y-2 text-[10px] font-black uppercase italic">
                                <a href="https://evm-bridge.iota.org" target="_blank" rel="noreferrer" className="flex items-center justify-between bg-purple-600 p-3 rounded-xl hover:bg-purple-500 transition-all shadow-lg shadow-purple-500/20">IOTA-EVM-BRIDGE <ExternalLink size={12} /></a>
                                <a href="https://explorer.evm.iota.org" target="_blank" rel="noreferrer" className="flex items-center justify-between bg-white/5 p-3 rounded-xl border border-white/5 hover:border-white/20 transition-all">Explorer <ExternalLink size={12}/></a>
                            </div>
                        </div>
                    </aside>

{/* MAIN POLL GRID */}
<div className="lg:col-span-3 space-y-8">
    
    {/* TWO-TIER FILTER SYSTEM */}
    <div className="flex flex-col gap-4">
        {/* TOP ROW: SECTIONS (Cash, Politics, Freedom) */}
        <div className="flex bg-white/5 p-1 rounded-3xl border border-white/5 backdrop-blur-xl w-fit overflow-x-auto">
            {["All", "Cash", "Politics", "Freedom"].map(s => (
                <button 
                    key={s} 
                    onClick={() => setActiveSection(s)} 
                    className={`px-8 py-2 rounded-2xl font-black uppercase text-[9px] tracking-[0.2em] transition-all ${activeSection === s ? "bg-purple-600 text-white shadow-lg" : "text-gray-500 hover:text-white"}`}
                >
                    {s}
                </button>
            ))}
        </div>

        {/* BOTTOM ROW: DIFFICULTY & STATUS (Easy, Highroller, Won) */}
        <div className="flex bg-white/5 p-1 rounded-3xl border border-white/5 backdrop-blur-xl w-fit overflow-x-auto">
            {["All", "Easy", "Highroller"].map(f => (
                <button 
                    key={f} 
                    onClick={() => setActiveFilter(f)} 
                    className={`px-10 py-3 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] transition-all ${activeFilter === f ? "bg-white text-black shadow-xl" : "text-gray-500 hover:text-white"}`}
                >
                    {f}
                </button>
            ))}
        </div>
    </div>

{account ? (
    <div className="grid gap-12">
        {/* 1. INITIAL BLOCKCHAIN SYNC SPINNER */}
        {isInitialLoading ? (
            <div className="flex flex-col items-center justify-center py-32 bg-[#0d0d12]/30 rounded-[3rem] border border-white/5 shadow-2xl">
                <div className="relative w-20 h-20">
                    <div className="absolute inset-0 border-4 border-purple-500/10 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-t-purple-600 rounded-full animate-spin"></div>
                </div>
                <p className="mt-8 text-[10px] text-purple-400 font-black uppercase italic tracking-[0.4em] animate-pulse">
                    Syncing Battles...
                </p>
            </div>
        ) : (
            /* 2. MAIN POLL LIST */
            <AnimatePresence mode='popLayout'>
                {filteredPolls.map((poll) => {
                    const status = userStatus[poll.id];
                    const uL = window.BigInt(status?.uL || "0");
                    const uS = window.BigInt(status?.uS || "0");
                    const totalBI = window.BigInt(poll.totalLucis) + window.BigInt(poll.totalSheitan);
                    const sentiment = totalBI > 0n ? Number((window.BigInt(poll.totalLucis) * 100n) / totalBI) : 50;
                    
                    const winnerId = Number(poll.winner);
                    const userWon = poll.resolved && ((winnerId === 1 && uL > 0n) || (winnerId === 2 && uS > 0n));
                    const isHighRoller = topPots.some(tp => tp.id === poll.id);
                    const pollImg = POLL_IMAGES[poll.id] || `https://api.dicebear.com{poll.id}`;

                    return (
                        <motion.div 
                            layout 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }} 
                            key={poll.id} 
                            ref={(el) => (pollRefs.current[poll.id] = el)} 
                            className="relative"
                        >
                            <div className={`rounded-[3.1rem] p-[2px] transition-all duration-500 ${isHighRoller ? 'bg-gradient-to-r from-yellow-500 via-amber-200 to-yellow-600 shadow-[0_0_35px_rgba(245,158,11,0.25)]' : 'bg-white/5 shadow-2xl'}`}>
                                <div className={`bg-[#0d0d12] rounded-[3rem] p-8 relative overflow-hidden ${isHighRoller ? 'highroller-glow' : ''} ${userWon ? 'border border-amber-500/50 shadow-[0_0_20px_rgba(245,158,11,0.1)]' : ''}`}>
                                    <div className="flex flex-col md:flex-row gap-10 items-center relative z-10">
                                        
                                        {/* IMAGE SECTION */}
                                        <div className="relative">
                                            <img src={pollImg} alt="P" className="w-40 h-40 rounded-[2.5rem] border border-white/10 shadow-2xl object-cover" />
                                            <div className="absolute -top-3 -left-3 bg-black border border-white/10 px-3 py-1 rounded-full text-[10px] font-black text-purple-400 shadow-xl">#{poll.id}</div>
                                        </div>

                                        <div className="flex-1 w-full text-center md:text-left">
                                            {/* INFO ROW */}
                                            <div className="flex flex-wrap gap-4 mb-4 justify-center md:justify-start items-center text-[10px] font-black uppercase tracking-widest text-gray-500 italic">
                                                <span className="bg-purple-500/10 border border-purple-500/20 text-purple-400 px-3 py-1 rounded-full">{poll.category}</span>
                                                <span className="bg-white/5 border border-white/5 px-3 py-1 rounded-full flex items-center gap-1.5">
                                                    <Clock size={12} className={poll.endTime - now < 3600 && poll.endTime > now ? "text-rose-500 animate-pulse" : ""}/> 
                                                    {poll.endTime > now ? (
    (() => {
        const diff = poll.endTime - now;
        const d = Math.floor(diff / 86400);
        const h = Math.floor((diff % 86400) / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = diff % 60;
        return `${d > 0 ? d + 'D ' : ''}${h > 0 || d > 0 ? h + 'H ' : ''}${m}M ${d === 0 && h === 0 ? s + 'S' : ''}`;
    })()
) : <span className="text-rose-500 font-bold">SETTLED</span>}
                                                </span>
                                                <button onClick={() => handleShare(poll.id)} className="bg-purple-600/10 border border-purple-500/30 text-purple-400 px-3 py-1 rounded-full flex items-center gap-1.5 transition-all active:scale-95 group">
                                                    <ExternalLink size={12} /> <span>SHARE</span>
                                                </button>
                                                <span className="text-purple-400 bg-purple-500/5 px-3 py-1 rounded-full border border-purple-500/10 font-bold">
                                                    Pot: {Number(ethers.formatUnits(totalBI.toString(), 18)).toLocaleString(undefined, {maximumFractionDigits: 0})} LIS/SEN
                                                </span>
                                            </div>

                                            <h2 className="text-3xl font-black uppercase italic mb-8 tracking-tighter text-white">{poll.question}</h2>

                                            {/* PROGRESS BAR */}
                                            <div className="mb-10">
                                                <div className="flex justify-between text-[9px] font-black uppercase mb-2 tracking-widest italic">
                                                    <span className="text-purple-400">Lucis {sentiment}%</span>
                                                    <span className="text-rose-400">Sheitan {100 - sentiment}%</span>
                                                </div>
                                                <div className="h-4 bg-white/5 rounded-full overflow-hidden border border-white/5 p-1 shadow-inner">
                                                    <motion.div initial={{ width: "50%" }} animate={{ width: `${sentiment}%` }} transition={{ duration: 1.5, ease: "easeOut" }} className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full" />
                                                </div>
                                            </div>

                                            {/* BUTTON SECTION (VOTING & CLAIMING) */}
<div className="mt-8">
    {!poll.resolved ? (
        poll.endTime <= now ? (
            <div className="w-full bg-amber-500/5 py-8 rounded-3xl border border-dashed border-amber-500/20 flex flex-col items-center justify-center gap-2 shadow-[0_0_20px_rgba(245,158,11,0.05)]">
                <div className="flex items-center gap-3 text-amber-500 font-black uppercase italic text-lg tracking-wider">
                    <Clock size={20} className="animate-pulse" /> POLL SETTLED
                </div>
                <p className="text-[10px] text-amber-500/50 font-bold uppercase tracking-[0.2em]">Waiting for final resolution...</p>
            </div>
        ) : status?.voted ? (
            <div className="w-full bg-white/5 py-6 rounded-2xl border border-dashed border-white/10 flex flex-col items-center justify-center gap-1">
                <div className="flex items-center gap-2 text-gray-500 font-black uppercase italic text-[10px] tracking-[0.3em]">
                    <Clock size={12} className="animate-pulse" /> Position Locked
                </div>
            </div>
        ) : (
      <div className="mt-8">
    {!poll.resolved ? (
        poll.endTime <= now ? (
            <div className="w-full bg-amber-500/5 py-8 rounded-3xl border border-dashed border-amber-500/20 flex flex-col items-center justify-center gap-2 shadow-[0_0_20px_rgba(245,158,11,0.05)]">
                <div className="flex items-center gap-3 text-amber-500 font-black uppercase italic text-lg tracking-wider">
                    <Clock size={20} className="animate-pulse" /> POLL SETTLED
                </div>
                <p className="text-[10px] text-amber-500/50 font-bold uppercase tracking-[0.2em]">Waiting for final resolution...</p>
            </div>
        ) : status?.voted ? (
            <div className="w-full bg-white/5 py-6 rounded-2xl border border-dashed border-white/10 flex flex-col items-center justify-center gap-1">
                <div className="flex items-center gap-2 text-gray-500 font-black uppercase italic text-[10px] tracking-[0.3em]">
                    <Clock size={12} className="animate-pulse" /> Position Locked
                </div>
            </div>
        ) : (
            <div className="grid grid-cols-2 gap-4">
                {/* LIS (LUCIS) BUTTON */}
                <motion.button 
                    disabled={loading} 
                    whileHover={{y:-2, scale: 1.02}} 
                    onClick={() => {
                        const reqL = window.BigInt(poll.reqL);
                        const balL = (typeof balances.lucis === 'string' && !balances.lucis.includes('.')) 
                            ? window.BigInt(balances.lucis) 
                            : ethers.parseUnits(balances.lucis || "0", 18);

                        if (balL < reqL) {
                            return setErrorPopup(`Insufficient $LIS. Have: ${ethers.formatUnits(balL, 18)} / Need: ${ethers.formatUnits(reqL, 18)}`);
                        }
                        handleAction(poll.id, true);
                    }} 
                    className="bg-emerald-500 text-black font-black py-5 rounded-2xl text-base md:text-xl shadow-xl uppercase italic flex items-center justify-center min-h-[64px]"
                >
                    {loading ? <div className="h-6 w-6 border-4 border-black/20 border-t-black rounded-full animate-spin" /> : `LIS (${ethers.formatUnits(poll.reqL.toString(), 18).split('.')[0]})`}
                </motion.button>

                {/* SEN (SHEITAN) BUTTON */}
                <motion.button 
                    disabled={loading} 
                    whileHover={{y:-2, scale: 1.02}} 
                    onClick={() => {
                        const reqS = window.BigInt(poll.reqS);
                        const balS = (typeof balances.sheitan === 'string' && !balances.sheitan.includes('.')) 
                            ? window.BigInt(balances.sheitan) 
                            : ethers.parseUnits(balances.sheitan || "0", 18);

                        if (balS < reqS) {
                            return setErrorPopup(`Insufficient $SEN. Have: ${ethers.formatUnits(balS, 18)} / Need: ${ethers.formatUnits(reqS, 18)}`);
                        }
                        handleAction(poll.id, false);
                    }} 
                    className="bg-rose-500 text-black font-black py-5 rounded-2xl text-base md:text-xl shadow-xl uppercase italic flex items-center justify-center min-h-[64px]"
                >
                    {loading ? <div className="h-6 w-6 border-4 border-black/20 border-t-black rounded-full animate-spin" /> : `SEN (${ethers.formatUnits(poll.reqS.toString(), 18).split('.')[0]})`}
                </motion.button>
            </div>
        )
    ) : (
        /* --- RESOLVED SECTION (CLAIMING) --- */
        userWon && !status?.claimed ? (
            <motion.button 
                disabled={loading} 
                onClick={() => handleAction(poll.id, false, true)} 
                className="w-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-black font-black py-6 rounded-3xl text-2xl flex items-center justify-center gap-4 uppercase italic shadow-[0_0_30px_rgba(245,158,11,0.3)] min-h-[80px]"
            >
                {loading ? <div className="h-8 w-8 border-4 border-black/20 border-t-black rounded-full animate-spin" /> : <><Gift size={28} /> <span>CLAIM REWARDS</span></>}
            </motion.button> 
        ) : (
            <div className="w-full py-6 rounded-3xl border border-emerald-500/30 bg-emerald-500/10 text-center flex items-center justify-center gap-2 text-emerald-500 font-black uppercase italic text-sm tracking-widest">
                {status?.claimed ? <><CheckCircle2 size={18} /> CLAIMED ✓</> : "Finished"}
            </div>
        )
    )}
</div>

        )
    ) : (
        /* --- RESOLVED SECTION (CLAIMING) --- */
        userWon && !status?.claimed ? (
            <motion.button 
                disabled={loading} 
                onClick={() => handleAction(poll.id, false, true)} 
                className="w-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-black font-black py-6 rounded-3xl text-2xl flex items-center justify-center gap-4 uppercase italic shadow-[0_0_30px_rgba(245,158,11,0.3)] min-h-[80px]"
            >
                {/* GIFT ICON USED HERE */}
                {loading ? <div className="h-8 w-8 border-4 border-black/20 border-t-black rounded-full animate-spin" /> : <><Gift size={28} /> <span>CLAIM REWARDS</span></>}
            </motion.button> 
        ) : (
            <div className="w-full py-6 rounded-3xl border border-emerald-500/30 bg-emerald-500/10 text-center flex items-center justify-center gap-2 text-emerald-500 font-black uppercase italic text-sm tracking-widest">
                {/* CHECKCIRCLE2 ICON USED HERE */}
                {status?.claimed ? <><CheckCircle2 size={18} /> CLAIMED ✓</> : "Finished"}
            </div>
        )
    )}
</div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        )}
    </div>
) : (
    /* --- BATTLES LOCKED / UNCONNECTED STATE --- */
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center py-24 border-2 border-dashed border-white/5 rounded-[3.5rem] bg-[#0d0d12]/50 backdrop-blur-md shadow-2xl text-center">
        <div className="bg-purple-600/10 p-6 rounded-full mb-6 relative">
            <Wallet size={48} className="text-purple-500 animate-pulse" />
        </div>
        <h2 className="text-2xl font-black uppercase italic tracking-tighter mb-3">Battles Locked</h2>
        <button onClick={connectWallet} className="bg-white text-black px-12 py-4 rounded-2xl font-black uppercase italic hover:bg-purple-500 transition-all shadow-xl">Unlock Battles</button>
    </motion.div>
)}

                    </div>
                </div>
            </main>



            <footer className="max-w-7xl mx-auto px-6 py-12 border-t border-white/5 mt-20">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        {/* Links & Copyright */}
        <div className="text-gray-500 text-[10px] font-black uppercase italic tracking-widest space-y-2">
            <p>© 2026 LissenVote Ecosystem</p>

        </div>

        {/* Final Legal Shield Block */}
        <div className="bg-white/5 p-6 rounded-3xl border border-white/5">
            <p className="text-[9px] text-gray-500 leading-relaxed uppercase font-bold italic">
                <span className="text-purple-400">Disclaimer:</span> LissenVote is a decentralized social experiment. 
                $LIS and $SEN are utility tokens with <span className="text-white">no financial value</span>. 
                Participation involves interacting with experimental smart contracts. 
                The developers are not responsible for any lost gas fees or technical failures. 
                By using this app, you confirm you are 18+ and understand the risks of blockchain technology.
            </p>
        </div>
    </div>
</footer>
 {/* DAS POPUP KOMMT HIERHIN (AUSSERHALB DER MAIN/POLLS) */}
            <AnimatePresence>
                {errorPopup && (
                    <motion.div 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[10000] bg-black/90 backdrop-blur-md flex items-center justify-center p-6 text-white text-center"
                    >
                        <motion.div 
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="max-w-md w-full bg-[#0d0d12] border border-purple-500/30 p-10 rounded-[3rem] shadow-2xl shadow-purple-500/10"
                        >
                            <div className="bg-purple-600/20 w-20 h-20 rounded-[2rem] flex items-center justify-center mx-auto mb-8 border border-purple-500/30">
                                <Coins size={40} className="text-purple-400" />
                            </div>

                            <h2 className="text-2xl font-black uppercase italic mb-4 tracking-tighter text-purple-500">
                                Insufficient Balance
                            </h2>
                            
                            <p className="text-[11px] text-gray-400 font-black uppercase italic leading-relaxed mb-10 tracking-widest">
                                {errorPopup}
                            </p>

                            <button 
                                onClick={() => setErrorPopup(null)} 
                                className="w-full bg-purple-600 hover:bg-purple-500 text-white font-black py-5 rounded-full transition-all uppercase italic tracking-widest shadow-xl active:scale-95 shadow-purple-500/20 border border-white/10"
                            >
                                GOT IT
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div> // Das ist das allerletzte schließende Div der App
    );
}

export default App;
